import axios from "axios";
import { httpInstanceCommerce } from "@/services/httpInstance";
import type { AssistedAffiliationDocumentRequest } from "./types";

export class AssistedAffiliationUploadError extends Error {}

// S3 recibe el binario sin credenciales ni interceptores de la API.
const s3Client = axios.create({ timeout: 120000 });
const CONTENT_TYPES: Record<string, string> = {
  jpeg: "image/jpeg",
  png: "image/png",
  pdf: "application/pdf",
};

const presign = async (documentNumber: string, file: string) => {
  if (!documentNumber.trim()) throw new Error("Missing document number");

  const { data } = await httpInstanceCommerce.post<{
    uploadUrl: string;
    fileUrl: string;
    fields: { path: string; method: string };
  }>("/document_link/v1/presign", {
    documentNumber: documentNumber.trim(), channel: "COMM", process: "AAP", file,
  });

  return data;
};

export const validateAssistedAffiliationFile = (file: File) => {
  const extension = file.name.match(/\.([^.]+)$/)?.[1].toLowerCase() ?? "";
  const suffix = extension === "jpg" ? "jpeg" : extension;
  const contentType = CONTENT_TYPES[suffix];

  if (!contentType || file.type !== contentType || file.size > 25 * 1024 * 1024) {
    throw new AssistedAffiliationUploadError("Revisa los archivos adjuntos: deben ser JPG, PNG o PDF de hasta 25MB.");
  }
  return { suffix, contentType };
};

export const uploadAssistedAffiliationFileService = async (
  documentNumber: string,
  name: string,
  file: File
): Promise<{ path: string }> => {
  const { suffix, contentType } = validateAssistedAffiliationFile(file);

  try {
    const data = await presign(documentNumber, `${name}.${suffix}`);
    if (!data?.uploadUrl || !data.fields?.path || data.fields.method !== "PUT") {
      throw new Error("Invalid presign response");
    }
    await s3Client.put(data.uploadUrl, file, { headers: { "Content-Type": contentType } });
    return { path: data.fields.path };
  } catch {
    // Axios contiene el archivo y la URL firmada; presentar solo el mensaje seguro.
    throw new AssistedAffiliationUploadError("No se pudieron subir los archivos. Intenta guardar nuevamente.");
  }
};

const readLatestDocument = async (
  documentNumber: string,
  document: AssistedAffiliationDocumentRequest
): Promise<string | undefined> => {
  let latest: { url: string; modified: number } | undefined;
  let accessDenied = false;

  for (const file of document.files) {
    const data = await presign(documentNumber, file);
    if (!data?.fileUrl || new URL(data.fileUrl).protocol !== "https:") {
      throw new Error("Invalid read URL");
    }
    try {
      const response = await s3Client.get(data.fileUrl, {
        responseType: "blob",
        headers: { Range: "bytes=0-0" },
      });
      const modified = Date.parse(response.headers?.["last-modified"] ?? "") || 0;
      if (!latest || modified > latest.modified) latest = { url: data.fileUrl, modified };
    } catch (error) {
      const status = (error as { response?: { status?: number } })?.response?.status;
      // Solo los formatos sin ruta guardada pueden faltar; red/CORS siempre es un error.
      if (document.hasSavedPath || (status !== 403 && status !== 404)) throw error;
      if (status === 403) accessDenied = true;
    }
  }

  if (!latest && accessDenied) throw new Error("Could not confirm access to identification photos");
  return latest?.url;
};

export const getAssistedAffiliationDocumentsService = async (
  documentNumber: string,
  documents: AssistedAffiliationDocumentRequest[]
): Promise<Record<string, string>> => {
  try {
    const links: Record<string, string> = {};
    await Promise.all(documents.map(async document => {
      const url = await readLatestDocument(documentNumber, document);
      if (url) links[document.name] = url;
    }));
    return links;
  } catch {
    throw new AssistedAffiliationUploadError("No se pudieron recuperar los archivos guardados. Vuelve a abrir la solicitud para reintentar.");
  }
};
